/**
 * OrgChart Resource
 *
 * Thin 1:1 mapping to the org chart API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  Position,
  PositionTree,
  CreatePositionRequest,
  UpdatePositionRequest,
  MovePositionRequest,
  StaffPositionRequest,
} from '../types/index.js';

export class OrgChartResource {
  constructor(private http: HttpLayer) {}

  private prefix = '/v1/orgchart';

  async getOrgChart(): Promise<PositionTree> {
    return this.http.get(`${this.prefix}/tree`);
  }

  async listPositions(): Promise<Position[]> {
    return this.http.get(`${this.prefix}/positions`);
  }

  async getPosition(positionId: string): Promise<Position> {
    return this.http.get(`${this.prefix}/positions/${positionId}`);
  }

  /**
   * Create an OCCUPIED org-chart seat. A position is occupied by exactly one of:
   * a person (`user_id`), or an Agent (`agent_id` AND `agent_process_id`).
   * There are no vacant seats and no half-staffed seats — omitting an occupant,
   * or naming an Agent without its agent process, is a 422
   * (work_management ADR-032).
   *
   * The Agent must already exist and must not be deleted. To staff from a
   * persona (its default Agent), or to have the agent process resolved for you,
   * use {@link OrgChartResource.staffPosition}.
   */
  async createPosition(params: CreatePositionRequest): Promise<Position> {
    return this.http.post(`${this.prefix}/positions`, params);
  }

  /**
   * Make a role executable: resolve an EXISTING Agent, resolve an agent
   * process the same way `chat.create` does (explicit id → the Agent's persona
   * default → seeded Simple), and write the Agent and the process onto the
   * position.
   *
   * Pass exactly one of `agent_id`, `agent_name`, `persona_id` or
   * `persona_name`; a persona staffs the seat with its default Agent. None, or
   * more than one, is a 422. This call does NOT invent an Agent or a persona: a
   * reference that does not exist, or names a deleted Agent, is a 422
   * (work_management ADR-032).
   *
   * Pass `position_id` to occupy an existing row, or `title` + `role` +
   * `team_id` (and `parent_id` for non-CEO) to create and staff in one call.
   * Never returns a vacant or half-staffed position.
   */
  async staffPosition(params: StaffPositionRequest): Promise<Position> {
    return this.http.post(`${this.prefix}/positions/staff`, params);
  }

  async updatePosition(
    positionId: string,
    params: UpdatePositionRequest,
  ): Promise<Position> {
    return this.http.patch(
      `${this.prefix}/positions/${positionId}`,
      params,
    );
  }

  async movePosition(
    positionId: string,
    params: MovePositionRequest,
  ): Promise<Position> {
    return this.http.post(
      `${this.prefix}/positions/${positionId}/move`,
      params,
    );
  }

  async getChildren(positionId: string): Promise<Position[]> {
    return this.http.get(
      `${this.prefix}/positions/${positionId}/children`,
    );
  }

  async deletePosition(
    positionId: string,
  ): Promise<{ deleted: boolean }> {
    return this.http.delete(
      `${this.prefix}/positions/${positionId}`,
    );
  }
}
